import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface ConsoleLineProps {
  text: string
  isTyping: boolean
  color?: string
  prefix?: string
  timestamp?: string
  onTypingComplete?: () => void
}

const TYPING_SPEED_MS = 25

export function ConsoleLine({
  text,
  isTyping,
  color,
  prefix,
  timestamp,
  onTypingComplete,
}: ConsoleLineProps) {
  const [displayedChars, setDisplayedChars] = useState(
    isTyping ? 0 : text.length,
  )

  useEffect(() => {
    if (!isTyping) {
      setDisplayedChars(text.length)
      return
    }

    setDisplayedChars(0)
    let current = 0

    const interval = setInterval(() => {
      current++
      setDisplayedChars(current)
      if (current >= text.length) {
        clearInterval(interval)
        onTypingComplete?.()
      }
    }, TYPING_SPEED_MS)

    return () => clearInterval(interval)
  }, [text, isTyping, onTypingComplete])

  const displayText = text.slice(0, displayedChars)
  const isComplete = displayedChars >= text.length

  return (
    <div
      className={cn(
        'font-mono text-sm leading-relaxed uppercase break-words',
        color || 'text-neutralColor60',
      )}
    >
      {timestamp && (
        <span className="text-neutralColor30 tabular-nums inline-block w-[8ch] mr-3">
          {timestamp}
        </span>
      )}
      {prefix && <span className="text-neutralColor60/40 mr-2">{prefix}</span>}
      <span>{displayText}</span>
      {isTyping && !isComplete && (
        <motion.span
          className="inline-block w-[6px] h-[14px] bg-dynamicGreen ml-0.5 align-middle"
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.53,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
      )}
    </div>
  )
}
